import { createTheme, alpha, type Theme } from '@mui/material/styles';

export type ColorMode = 'light' | 'dark';

// Brand teal, matching replayops.ai (--primary hsl 174 62% 47% → #2ec2b3),
// with a teal→cyan gradient and near-black text on the accent.
const TEAL = '#2ec2b3';
const TEAL_LIGHT = '#5fd4c8';
const TEAL_DARK = '#22988c';
const CYAN = '#26bbd9';
const ON_ACCENT = '#0a0c10';

/** ReplayOps theme — teal accent, light by default with a dark variant. */
export function makeTheme(mode: ColorMode): Theme {
  const light = mode === 'light';

  const bg = light ? '#f6f7f9' : '#0b0f14';
  const surface = light ? '#ffffff' : '#121820';
  const surface2 = light ? '#f1f5f9' : '#182029';
  const border = light ? '#e6e9ef' : 'rgba(255,255,255,0.08)';
  const borderStrong = light ? '#d3dae2' : 'rgba(255,255,255,0.16)';
  const ink = light ? '#131720' : '#e8edf2';
  const inkSoft = light ? '#6b7280' : '#9aa6b2';

  // Soft, layered elevation instead of MUI's heavy material shadows — this is
  // what gives the reference screens their calm "floating card" feel. Cards use
  // shadows[1]; hover/menus step up to [2]/[3].
  const sh = (a: number, b: number, y1: number, blur2: number) =>
    light
      ? `0 1px 2px rgba(16,24,40,${a}), 0 ${y1}px ${blur2}px rgba(16,24,40,${b})`
      : `0 1px 2px rgba(0,0,0,0.4), 0 ${y1}px ${blur2}px rgba(0,0,0,0.5)`;
  const shadows = [...createTheme().shadows] as Theme['shadows'];
  shadows[1] = sh(0.04, 0.06, 1, 3);
  shadows[2] = sh(0.05, 0.07, 2, 8);
  shadows[3] = sh(0.06, 0.09, 6, 16);

  return createTheme({
    shadows,
    palette: {
      mode,
      primary: { main: TEAL, light: TEAL_LIGHT, dark: TEAL_DARK, contrastText: ON_ACCENT },
      secondary: { main: '#0ea5e9' },
      success: { main: '#16a34a' },
      warning: { main: '#f59e0b' },
      error: { main: '#ef4444' },
      info: { main: '#0ea5e9' },
      background: { default: bg, paper: surface },
      divider: border,
      text: { primary: ink, secondary: inkSoft },
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      h4: { fontWeight: 800, letterSpacing: '-0.02em' },
      h5: { fontWeight: 700, letterSpacing: '-0.01em' },
      h6: { fontWeight: 700, letterSpacing: '-0.01em' },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: bg },
          '::-webkit-scrollbar': { width: 10, height: 10 },
          '::-webkit-scrollbar-thumb': {
            backgroundColor: borderStrong,
            borderRadius: 8,
            border: '2px solid transparent',
            backgroundClip: 'content-box',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none', border: `1px solid ${border}` },
          elevation0: { border: 'none' },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: surface,
            border: `1px solid ${border}`,
            transition: 'border-color .15s ease, box-shadow .15s ease',
            '&:hover': { borderColor: borderStrong },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: light ? alpha('#ffffff', 0.82) : alpha('#0b0f14', 0.75),
            backdropFilter: 'blur(10px)',
            borderBottom: `1px solid ${border}`,
            boxShadow: 'none',
            backgroundImage: 'none',
            color: ink,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { backgroundColor: surface, borderRight: `1px solid ${border}`, backgroundImage: 'none' },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { textTransform: 'none', borderRadius: 10 },
          containedPrimary: {
            color: ON_ACCENT,
            background: `linear-gradient(135deg, ${TEAL}, ${CYAN})`,
            '&:hover': { background: `linear-gradient(135deg, ${TEAL_DARK}, ${TEAL})` },
          },
          outlined: { borderColor: borderStrong },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            marginInline: 8,
            marginBlock: 2,
            '&.Mui-selected': {
              backgroundColor: alpha(TEAL, light ? 0.14 : 0.2),
              '&:hover': { backgroundColor: alpha(TEAL, light ? 0.18 : 0.26) },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: { root: { fontWeight: 600 }, outlined: { borderColor: borderStrong } },
      },
      MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600, minHeight: 44 } } },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: border },
          head: {
            color: inkSoft,
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            backgroundColor: surface2,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': { borderColor: border },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: borderStrong },
          },
        },
      },
      MuiDivider: { styleOverrides: { root: { borderColor: border } } },
    },
  });
}

/** Back-compat default (light) for any non-provider imports. */
export const theme = makeTheme('light');
